import { useState, useCallback, useRef } from "react";
import { Plus, X } from "lucide-react";

interface StickyNote {
  id: string;
  x: number;
  y: number;
  title: string;
  body: string;
  color: "amber" | "indigo" | "emerald" | "rose" | "violet" | "sky";
  rotation: number;
}

const colorStyles = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", title: "text-amber-900", body: "text-amber-700", shadow: "shadow-amber-100/50" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", title: "text-indigo-900", body: "text-indigo-700", shadow: "shadow-indigo-100/50" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", title: "text-emerald-900", body: "text-emerald-700", shadow: "shadow-emerald-100/50" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", title: "text-rose-900", body: "text-rose-700", shadow: "shadow-rose-100/50" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", title: "text-violet-900", body: "text-violet-700", shadow: "shadow-violet-100/50" },
  sky: { bg: "bg-sky-50", border: "border-sky-200", title: "text-sky-900", body: "text-sky-700", shadow: "shadow-sky-100/50" },
};

const colorOrder: StickyNote["color"][] = ["amber", "indigo", "emerald", "rose", "violet", "sky"];

const initialNotes: StickyNote[] = [
  { id: "1", x: 30, y: 40, title: "Project Ideas", body: "Brainstorm new features for Q2 launch. Think about user onboarding flow...", color: "amber", rotation: -2 },
  { id: "2", x: 500, y: 24, title: "Meeting Notes", body: "Sync across all devices. Record audio memos. Clip web pages for reference.", color: "indigo", rotation: 1 },
  { id: "3", x: 250, y: 300, title: "Research Notes", body: "Spatial computing helps memory retention by 40%. People remember WHERE they put things...", color: "emerald", rotation: 0.5 },
  { id: "4", x: 530, y: 320, title: "Todo", body: "Add tag filtering. Fix dark mode toggle.", color: "rose", rotation: -1.5 },
];

export function CanvasDemo() {
  const [notes, setNotes] = useState<StickyNote[]>(initialNotes);
  const [dragging, setDragging] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const getCanvasPos = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent, noteId: string) => {
    if (editing === noteId) return;
    e.preventDefault();
    e.stopPropagation();
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    const pos = getCanvasPos(e.clientX, e.clientY);
    setDragging({ id: noteId, offsetX: pos.x - note.x, offsetY: pos.y - note.y });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    // Bring to front
    setNotes((prev) => {
      const idx = prev.findIndex((n) => n.id === noteId);
      if (idx === -1) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(idx, 1);
      copy.push(moved);
      return copy;
    });
  }, [notes, editing, getCanvasPos]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const pos = getCanvasPos(e.clientX, e.clientY);
    const newX = pos.x - dragging.offsetX;
    const newY = pos.y - dragging.offsetY;
    setNotes((prev) =>
      prev.map((n) => (n.id === dragging.id ? { ...n, x: newX, y: newY } : n))
    );
  }, [dragging, getCanvasPos]);

  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  const addNote = useCallback(() => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.random() * (rect.width - 200) + 20;
    const y = Math.random() * (rect.height - 150) + 20;
    const color = colorOrder[notes.length % colorOrder.length];
    const rotation = (Math.random() - 0.5) * 4;
    const newNote: StickyNote = {
      id: Date.now().toString(),
      x, y,
      title: "New Note",
      body: "Click to edit...",
      color,
      rotation,
    };
    setNotes((prev) => [...prev, newNote]);
    setEditing(newNote.id);
  }, [notes.length]);

  const deleteNote = useCallback((id: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    if (editing === id) setEditing(null);
  }, [editing]);

  const updateNote = useCallback((id: string, field: "title" | "body", value: string) => {
    setNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, [field]: value } : n))
    );
  }, []);

  return (
    <section className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/50 overflow-hidden">
          {/* Window chrome */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
            <span className="ml-3 text-xs text-slate-400 font-medium">
              OmniCanvas
            </span>
            <button
              onClick={addNote}
              className="ml-auto flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-full transition-colors"
            >
              <Plus size={14} />
              Add Note
            </button>
          </div>

          {/* Interactive canvas */}
          <div
            ref={canvasRef}
            className="relative h-[400px] md:h-[500px] bg-[#fafafa] overflow-hidden select-none"
            style={{ touchAction: "none" }}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onClick={() => { if (!dragging) setEditing(null); }}
          >
            {/* Grid dots */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle, #94a3b8 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            />

            {/* Sticky notes */}
            {notes.map((note) => {
              const c = colorStyles[note.color];
              const isEditing = editing === note.id;
              const isDragging = dragging?.id === note.id;
              return (
                <div
                  key={note.id}
                  className={`absolute w-48 md:w-56 p-4 ${c.bg} ${c.border} border rounded-lg shadow-md ${c.shadow} transition-shadow ${isDragging ? "shadow-lg scale-105 z-50" : "hover:shadow-lg"}`}
                  style={{
                    left: note.x,
                    top: note.y,
                    transform: `rotate(${note.rotation}deg)${isDragging ? " scale(1.05)" : ""}`,
                    cursor: isEditing ? "text" : isDragging ? "grabbing" : "grab",
                    zIndex: isDragging ? 50 : undefined,
                  }}
                  onPointerDown={(e) => handlePointerDown(e, note.id)}
                  onDoubleClick={(e) => { e.stopPropagation(); setEditing(note.id); }}
                >
                  {/* Delete button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-white border border-slate-200 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity shadow-sm"
                    style={{ opacity: isEditing || isDragging ? 0 : undefined }}
                    onPointerEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                    onPointerLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0"; }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <X size={10} className="text-slate-500" />
                  </button>

                  {isEditing ? (
                    <>
                      <input
                        className={`text-sm font-semibold ${c.title} bg-transparent border-none outline-none w-full mb-1`}
                        value={note.title}
                        onChange={(e) => updateNote(note.id, "title", e.target.value)}
                        onPointerDown={(e) => e.stopPropagation()}
                        autoFocus
                      />
                      <textarea
                        className={`text-xs ${c.body} bg-transparent border-none outline-none w-full resize-none`}
                        value={note.body}
                        rows={3}
                        onChange={(e) => updateNote(note.id, "body", e.target.value)}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </>
                  ) : (
                    <>
                      <p className={`text-sm font-semibold ${c.title} mb-1`}>
                        {note.title}
                      </p>
                      <p className={`text-xs ${c.body}`}>
                        {note.body}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-8 max-w-lg mx-auto">
          <p className="text-slate-700 font-medium mb-2">
            Draw, sketch, and organize on a real infinite canvas — right in your browser.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            No sign-up required. Full drawing tools, templates, and more.
          </p>
          <a
            href="/demo"
            className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
          >
            Try the interactive demo
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </a>
        </div>
      </div>
    </section>
  );
}
